import React, { useState, useEffect, useContext, useRef } from 'react';
import { FirebaseContext } from './Firebase';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X } from '@styled-icons/boxicons-regular/X';
import { ArrowRight as Send } from '@styled-icons/icomoon/ArrowRight';
import { aiGenerations } from './FirebaseApp';
import ImageModal from './components/ImageModal';
import ImageFeedbackButtons from './components/ImageFeedbackButtons';
import { Sparkles } from '@styled-icons/heroicons-solid/Sparkles';

const ChatWithImage = ({ isOpen, onClose, imageUrl, originalImageId, currentSettings = {} }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);
  const [sessionId, setSessionId] = useState(null);
  const [selectedImageModal, setSelectedImageModal] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState({});
  const [autoEnhance, setAutoEnhance] = useState(true);
  const [engine, setEngine] = useState('qwen'); // 'qwen' | 'flux'
  const messagesEndRef = useRef(null);
  const fb = useContext(FirebaseContext);
  const { app, user } = fb;

  // Auto-scroll to bottom when new messages are added
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chat immediately when dialog opens
  useEffect(() => {
    if (imageUrl && isOpen && user) {
      initializeChat();
    }
  }, [imageUrl, isOpen, user]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      
      // Disable body scroll
      document.body.style.overflow = 'hidden';
      
      // Restore overflow when component unmounts or modal closes
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  const initializeChat = () => {
    // Show system message immediately - no waiting for API calls
    const systemMessage = {
      id: 'system-msg',
      type: 'system',
      content: 'Chat started with your mood board. Ask me to make changes to the image!',
      timestamp: new Date(),
      imageUrl: imageUrl,
      aiGeneratorId: originalImageId,
      feedback: {
        isUpscaled: false,
        isDownloaded: false
      }
    };
    
    setMessages([systemMessage]);
    setCurrentImageUrl(imageUrl);
    
    // Create session in background (non-blocking)
    createChatSessionInBackground();
  };

  const createChatSessionInBackground = async () => {
    try {
      const createChatSessionFn = app.functions().httpsCallable("createChatSession");
      
      const response = await createChatSessionFn({
        originalImageUrl: imageUrl,
        originalImageId: originalImageId,
        settings: { ...currentSettings, engine }
      });

      setSessionId(response.data.sessionId);
    } catch (error) {
      console.error("Error creating chat session:", error);
      // Session creation failed, but chat can still work without it
    }
  };

  // Ensure we have a session before sending message
  const ensureSession = async () => {
    if (sessionId) return sessionId;
    
    try {
      const createChatSessionFn = app.functions().httpsCallable("createChatSession");
      
      const response = await createChatSessionFn({
        originalImageUrl: imageUrl,
        originalImageId: originalImageId,
        settings: { ...currentSettings, engine }
      });

      const newSessionId = response.data.sessionId;
      setSessionId(newSessionId);
      return newSessionId;
    } catch (error) {
      console.error("Error creating chat session:", error);
      return null; // Can still send message without session
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !user) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    
    setIsLoading(true);
    
    const originalMessage = inputMessage;
    setInputMessage('');

    try {
      // Ensure we have a session (create if needed)
      const currentSessionId = await ensureSession();
      
      // Conditionally enhance the prompt based on autoEnhance toggle
      let finalPrompt = originalMessage; // fallback to original
      let enhancementWorked = false;
      
      if (autoEnhance) {
        try {
          const enhancePromptFn = app.functions().httpsCallable("enhanceContextPrompt");
          const promptResponse = await enhancePromptFn({
            userMessage: originalMessage,
            imageUrl: currentImageUrl,
            settings: { ...currentSettings, engine }
          });
          
          // Check if we got a valid enhanced prompt
          if (promptResponse.data.enhancedPrompt && promptResponse.data.enhancedPrompt.trim().length > 0) {
            finalPrompt = promptResponse.data.enhancedPrompt;
            enhancementWorked = true;
            console.log("Enhanced prompt:", finalPrompt);

            // Display the enhanced prompt in the chat
            const enhancedPromptMessage = {
              id: `enhanced-prompt-${Date.now()}`,
              type: 'enhanced-prompt',
              content: `Using enhanced prompt: "${finalPrompt}"`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, enhancedPromptMessage]);
          } else {
            console.warn("Enhancement returned empty result, using original prompt");
          }

        } catch (promptError) {
          console.warn("Failed to enhance prompt, using original:", promptError);
        }
      }
      
      // If enhancement was disabled or failed, show info message
      if (!autoEnhance || !enhancementWorked) {
        const promptInfoMessage = {
          id: `prompt-info-${Date.now()}`,
          type: 'enhanced-prompt', // Use same styling for consistency
          content: autoEnhance 
            ? `Proceeding with original prompt: "${originalMessage}" (enhancement failed).`
            : `Using original prompt: "${originalMessage}"`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, promptInfoMessage]);
      }
      
      // Set loading true *after* potentially showing the enhanced prompt
      setIsLoading(true);

      // Use the enhanced chatWithImage function with session support
      const chatWithImageFn = app.functions().httpsCallable("chatWithImageEnhanced");
      
      const requestData = {
        prompt: finalPrompt, // Use the final prompt (either enhanced or original)
        imageUpload: currentImageUrl,
        groupId: "",
        sessionId: currentSessionId,
        type: currentSettings.type || "",
        roomType: currentSettings.roomType || "",
        style: currentSettings.style || "",
        colorCombination: currentSettings.colorCombination || "",
        engine
      };

      const response = await chatWithImageFn(requestData);
      const predictionId = response.data.id;

      // Set up real-time listener for the result
      const aiRef = await aiGenerations();
      const unsubscribe = aiRef.doc(predictionId).onSnapshot((doc) => {
        if (doc.exists) {
          const data = doc.data();
          
          if (data.status === "SUCCESS" && data.image) {
            const assistantMessage = {
              id: predictionId,
              type: 'assistant',
              content: `I've made the changes you requested. Here's the updated image:`,
              timestamp: new Date(Date.now() + 1), // Ensure different timestamp
              imageUrl: data.image,
              aiGeneratorId: predictionId,
              feedback: {
                isUpscaled: false,
                isDownloaded: false
              }
            };
            
            setMessages(prev => [...prev, assistantMessage]);
            setCurrentImageUrl(data.image);
            setIsLoading(false);
            
            unsubscribe();
          } else if (data.status === "FAILURE") {
            const errorMessage = {
              id: `error-${Date.now()}`,
              type: 'assistant',
              content: "Sorry, I couldn't process your request. Please try again with a different description.",
              timestamp: new Date()
            };
            
            setMessages(prev => [...prev, errorMessage]);
            setIsLoading(false);
            
            unsubscribe();
          }
        }
      }, (error) => {
        console.error("Error listening to prediction:", error);
        const errorMessage = {
          id: `error-${Date.now()}`,
          type: 'assistant',
          content: "Sorry, there was an error processing your request. Please try again.",
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
      });

    } catch (error) {
      console.error("Error sending message:", error);
      
      let errorContent = "Sorry, there was an error processing your request. Please try again.";
      if (error.code === "unauthenticated") {
        errorContent = "You must be logged in to use this feature.";
      } else if (error.code === "resource-exhausted") {
        errorContent = "You have reached your AI generation limit. Please upgrade your plan.";
      }
      
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'assistant',
        content: errorContent,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleImageFeedback = async (action, message) => {
    if (!message) return;

    const messageId = message.id;
    const aiGeneratorId = message.aiGeneratorId;

    // Set loading state for specific button
    setFeedbackLoading(prev => ({ ...prev, [`${messageId}-${action}`]: true }));

    try {
      if (action === 'upscale') {
        // Handle upscale - for system messages, try to extract ID from imageUrl or use a different approach
        if (aiGeneratorId) {
          await import("firebase/functions");
          const upscaleFunction = app.functions().httpsCallable('upscaleImage');
          await upscaleFunction({ boardId: aiGeneratorId });

          // Listen for upscale completion to update the image
          const aiRef = await aiGenerations();
          const unsubscribe = aiRef.doc(aiGeneratorId).onSnapshot((doc) => {
            if (doc.exists) {
              const data = doc.data();
              if (data.upscaledImageUrl && !data.isUpscaling) {
                // Update message with upscaled image URL and mark as upscaled
                setMessages(prev => prev.map(msg => 
                  msg.id === messageId 
                    ? { 
                        ...msg, 
                        imageUrl: data.upscaledImageUrl,
                        feedback: { ...msg.feedback, isUpscaled: true }
                      }
                    : msg
                ));

                // Update modal if it's the same message
                setSelectedImageModal(prev => 
                  prev && prev.id === messageId 
                    ? { 
                        ...prev, 
                        imageUrl: data.upscaledImageUrl,
                        feedback: { ...prev.feedback, isUpscaled: true }
                      }
                    : prev
                );

                setFeedbackLoading(prev => ({ ...prev, [`${messageId}-${action}`]: false }));
                unsubscribe();
              }
            }
          }, (error) => {
            console.error("Error listening to upscale:", error);
            setFeedbackLoading(prev => ({ ...prev, [`${messageId}-${action}`]: false }));
          });
        } else {
          // For system messages without aiGeneratorId, show a message that upscale isn't available
          console.warn("Cannot upscale: No aiGeneratorId available for this image");
          setFeedbackLoading(prev => ({ ...prev, [`${messageId}-${action}`]: false }));
        }
      } else if (action === 'download') {
        // Handle download
        if (message.imageUrl) {
          const link = document.createElement('a');
          link.href = message.imageUrl;
          link.download = `mood-board-${messageId}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Update download status
          setMessages(prev => prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, feedback: { ...msg.feedback, isDownloaded: true } }
              : msg
          ));

          // Update modal if it's the same message
          setSelectedImageModal(prev => 
            prev && prev.id === messageId 
              ? { ...prev, feedback: { ...prev.feedback, isDownloaded: true } }
              : prev
          );
        }
      }

      // Clear loading state for non-upscale actions
      if (action !== 'upscale') {
        setFeedbackLoading(prev => ({ ...prev, [`${messageId}-${action}`]: false }));
      }
    } catch (error) {
      console.error(`Error handling ${action}:`, error);
      
      // Clear loading state on error
      if (action !== 'upscale') {
        setFeedbackLoading(prev => ({ ...prev, [`${messageId}-${action}`]: false }));
      }
    }
  };

  const handleImageClick = async (message) => {
    setSelectedImageModal(message);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    const resetSystemMessage = {
      id: 'system-msg',
      type: 'system',
      content: 'Chat started with your mood board. Ask me to make changes to the image!',
      timestamp: new Date(),
      imageUrl: imageUrl,
      aiGeneratorId: originalImageId,
      feedback: {
        isUpscaled: false,
        isDownloaded: false
      }
    };
    
    setMessages([resetSystemMessage]);
    
    setCurrentImageUrl(imageUrl);
    setInputMessage('');
    setSessionId(null);
    // Re-create session in background
    createChatSessionInBackground();
  };

  const undoLastGeneration = () => {
    // Find the last user message, enhanced prompt message, and last assistant message
    let lastUserMessage = null;
    let lastAssistantMessage = null;
    let enhancedPromptMessage = null;
    
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.type === 'assistant' && !lastAssistantMessage) {
        lastAssistantMessage = message;
      } else if (message.type === 'enhanced-prompt' && !enhancedPromptMessage && lastAssistantMessage) {
        // Only consider enhanced prompt if we found an assistant message first
        enhancedPromptMessage = message;
      } else if (message.type === 'user' && !lastUserMessage && lastAssistantMessage) {
        // Only consider user message if we found an assistant message first
        lastUserMessage = message;
      }
      
      // Stop when we found the user message (we work backwards)
      if (lastUserMessage && lastAssistantMessage) break;
    }
    
    if (!lastUserMessage || !lastAssistantMessage) return;
    
    // Create array of message IDs to remove
    const messageIdsToRemove = [lastUserMessage.id, lastAssistantMessage.id];
    if (enhancedPromptMessage) {
      messageIdsToRemove.push(enhancedPromptMessage.id);
    }
    
    // Remove the last user, enhanced prompt (if exists), and assistant messages
    const updatedMessages = messages.filter(msg => 
      !messageIdsToRemove.includes(msg.id)
    );
    
    // Set the user's message back to the input field
    setInputMessage(lastUserMessage.content);
    
    // Update messages and current image
    setMessages(updatedMessages);
    
    // Find the previous image to set as current
    let previousImageUrl = imageUrl; // fallback to original
    for (let i = updatedMessages.length - 1; i >= 0; i--) {
      if (updatedMessages[i].imageUrl) {
        previousImageUrl = updatedMessages[i].imageUrl;
        break;
      }
    }
    setCurrentImageUrl(previousImageUrl);
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 xl:overflow-y-auto">
            <div className="flex h-full w-full xl:min-h-full xl:items-center xl:justify-center xl:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full h-full xl:w-full xl:max-w-7xl xl:h-auto xl:max-h-[90vh] xl:rounded-2xl transform bg-[#fdfdfc] p-4 md:p-6 text-left align-middle shadow-xl transition-all flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-heading text-gray-900"
                    >
                      Chat with Image
                    </Dialog.Title>
                    <div className="flex items-center gap-2">
                      {/* Engine toggle */}
                      <div className="hidden md:flex items-center gap-2 mr-2">
                        <span className="text-xs text-gray-400">Engine</span>
                        <div className="bg-gray-100 rounded-full p-1 flex items-center">
                          <button
                            onClick={() => setEngine('qwen')}
                            className={`px-3 py-1.5 text-xs rounded-full font-medium text-center ${engine === 'qwen' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                            title="Use Qwen Image Edit (default)"
                          >
                            Qwen
                          </button>
                          <button
                            onClick={() => setEngine('flux')}
                            className={`px-3 py-1.5 text-xs rounded-full font-medium text-center ${engine === 'flux' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                            title="Use Flux Kontext Pro"
                          >
                            Flux
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={resetChat}
                        className="text-gray-500 hover:text-gray-700 px-3 py-1 text-sm rounded-md border border-gray-200 hover:bg-gray-50"
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600"
                        onClick={onClose}
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto mb-4 border border-gray-200 rounded-lg bg-white min-h-0 xl:h-[42rem]">
                    <div className="p-4 space-y-4">
                      {messages.map((message, index) => (
                        <div
                          key={message.id || index}
                          className={`flex ${
                            message.type === 'user' ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-2xl px-4 py-2 rounded-lg ${
                              message.type === 'user'
                                ? 'bg-pink-600 text-white'
                                : message.type === 'system'
                                ? 'bg-gray-100 text-gray-700'
                                : 'bg-gray-200 text-gray-800'
                            }`}
                          >
                            <p className="text-sm">{message.content}</p>
                            {message.imageUrl && (
                              <div className="mt-3">
                                <img
                                  src={message.imageUrl}
                                  alt="Generated result"
                                  className="w-full rounded-lg max-w-sm sm:max-w-2xl mx-auto cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => handleImageClick(message)}
                                />
                                {/* Feedback buttons for images */}
                                {(message.type === 'assistant' || message.type === 'system') && message.imageUrl && (
                                  <ImageFeedbackButtons
                                    message={message}
                                    onFeedback={handleImageFeedback}
                                    feedbackLoading={feedbackLoading}
                                    isLoading={Object.values(feedbackLoading).some(loading => loading)}
                                    onUndo={undoLastGeneration}
                                    isLastGeneration={index === messages.length - 1 && message.type === 'assistant'}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 text-gray-700 px-4 py-3 rounded-lg max-w-sm lg:max-w-md border border-gray-200">
                            <div className="flex items-center gap-3">
                              {/* Simple animated dots */}
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                                <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                                <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                              </div>
                              <span className="text-sm font-medium">Generating image...</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                  </div>

                  {/* Input Area */}
                  <div className="flex gap-2 flex-shrink-0">
                    <div className="flex-1 relative">
                      <textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Describe the changes you'd like to make to the image..."
                        className="w-full px-4 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                        rows={2}
                        disabled={isLoading}
                        data-hj-allow=""
                      />
                      {/* Auto Enhance Toggle Badge */}
                      <button
                        onClick={() => setAutoEnhance(!autoEnhance)}
                        className={`absolute right-2 top-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                          autoEnhance 
                            ? 'bg-pink-100 text-pink-700 border border-pink-200 hover:bg-pink-200' 
                            : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                        }`}
                        title={autoEnhance ? "Auto enhance is ON - prompts will be automatically enhanced" : "Auto enhance is OFF - prompts will be used as-is"}
                      >
                        <Sparkles className={`h-3 w-3 ${autoEnhance ? 'text-pink-600' : 'text-gray-500'}`} />
                        <span>{autoEnhance ? 'Enhance' : 'Off'}</span>
                      </button>
                    </div>
                    <button
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || isLoading}
                      className="bg-pink-600 hover:bg-pink-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Image Modal */}
      <ImageModal
        isOpen={!!selectedImageModal}
        onClose={() => setSelectedImageModal(null)}
        message={selectedImageModal}
      >
        {selectedImageModal && (
          <ImageFeedbackButtons
            message={selectedImageModal}
            onFeedback={handleImageFeedback}
            size="large"
            feedbackLoading={feedbackLoading}
            isLoading={Object.values(feedbackLoading).some(loading => loading)}
            onUndo={undoLastGeneration}
            isLastGeneration={messages[messages.length - 1]?.id === selectedImageModal.id && selectedImageModal.type === 'assistant'}
          />
        )}
      </ImageModal>
    </>
  );
};

export default ChatWithImage; 